import React, { useMemo, useState, useCallback } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import { pickImageFromCamera, pickImageWithChoice } from "./imagePicker";
import { commonStyles, vehicleStyles as styles } from "./styles";
import {
  uploadOnboardingImage,
  resubmitDocuments,
} from "@services/onboardingApi";
import { useFlowNavigation } from "./useFlowNavigation";
import type { RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList } from "./types";
import type { DocumentId } from "./flowConfig.types";

interface ReuploadDocumentsProps {
  route: RouteProp<OnboardingStackParamList, "ReuploadDocuments">;
  navigation: StackNavigationProp<
    OnboardingStackParamList,
    "ReuploadDocuments"
  >;
}

type UploadKey =
  | "aadhaarCard:front"
  | "aadhaarCard:back"
  | "panCard:front"
  | "panCard:back"
  | "drivingLicense:front"
  | "drivingLicense:back"
  | "photo"
  | "personalDoc:front"
  | "personalDoc:back";

// Map legacy kyc_item_name → UploadKey
const ITEM_NAME_TO_KEY: Record<string, UploadKey> = {
  aadhar_front: "aadhaarCard:front",
  aadhar_back: "aadhaarCard:back",
  pan_image: "panCard:front",
  pan_back: "panCard:back",
  driving_license_front: "drivingLicense:front",
  driving_license_back: "drivingLicense:back",
  user_photo: "photo",
  personal_doc_front: "personalDoc:front",
  personal_doc_back: "personalDoc:back",
};

interface UrlState {
  aadhaarFront: string | null;
  aadhaarBack: string | null;
  panFront: string | null;
  panBack: string | null;
  dlFront: string | null;
  dlBack: string | null;
  selfie: string | null;
  personalDocFront: string | null;
  personalDocBack: string | null;
}

const KEY_TO_URL_FIELD: Record<UploadKey, keyof UrlState> = {
  "aadhaarCard:front": "aadhaarFront",
  "aadhaarCard:back": "aadhaarBack",
  "panCard:front": "panFront",
  "panCard:back": "panBack",
  "drivingLicense:front": "dlFront",
  "drivingLicense:back": "dlBack",
  photo: "selfie",
  "personalDoc:front": "personalDocFront",
  "personalDoc:back": "personalDocBack",
};

const DOC_KEY_MAP: Record<
  DocumentId,
  {
    front: { uploadKey: UploadKey; urlField: keyof UrlState };
    back: { uploadKey: UploadKey; urlField: keyof UrlState };
  }
> = {
  aadhaarCard: {
    front: { uploadKey: "aadhaarCard:front", urlField: "aadhaarFront" },
    back: { uploadKey: "aadhaarCard:back", urlField: "aadhaarBack" },
  },
  panCard: {
    front: { uploadKey: "panCard:front", urlField: "panFront" },
    back: { uploadKey: "panCard:back", urlField: "panBack" },
  },
  drivingLicense: {
    front: { uploadKey: "drivingLicense:front", urlField: "dlFront" },
    back: { uploadKey: "drivingLicense:back", urlField: "dlBack" },
  },
};

const INITIAL_URLS: UrlState = {
  aadhaarFront: null,
  aadhaarBack: null,
  panFront: null,
  panBack: null,
  dlFront: null,
  dlBack: null,
  selfie: null,
  personalDocFront: null,
  personalDocBack: null,
};

const AADHAAR_REGEX = /^\d{12}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const normalizeAadhaar = (v?: string | null) => (v ?? "").replace(/\D/g, "");
const normalizePan = (v?: string | null) => (v ?? "").trim().toUpperCase();

const ReuploadDocuments: React.FC<ReuploadDocumentsProps> = ({
  route,
  navigation,
}) => {
  const {
    isFamilyKyc,
    phoneNumber,
    rejectedItems,
    aadhaarNumber: initialAadhaar,
    panNumber: initialPan,
  } = route.params;
  const { getDocumentsConfig } = useFlowNavigation();
  const documentConfigs = getDocumentsConfig();

  const { rejectedKeys, rejectionComments } = useMemo(() => {
    const keys = new Set<UploadKey>();
    const comments: Partial<Record<UploadKey, string>> = {};
    for (const item of rejectedItems ?? []) {
      const key = ITEM_NAME_TO_KEY[item.name];
      if (!key) continue;
      keys.add(key);
      if (item.comment) comments[key] = item.comment;
    }
    return { rejectedKeys: keys, rejectionComments: comments };
  }, [rejectedItems]);

  // Aadhaar/PAN NUMBERS are now editable on re-submission (prefilled with the
  // stored values). A backend rejection can target the number item directly
  // (aadhar_number / pan_number), which — unlike the image items — maps to no
  // upload tile, so we surface it as a rejected number field instead.
  const showAadhaarNumber = documentConfigs.some((d) => d.id === "aadhaarCard");
  const showPanNumber = documentConfigs.some((d) => d.id === "panCard");
  const aadhaarNumRejected = (rejectedItems ?? []).some(
    (i) => i.name === "aadhar_number",
  );
  const panNumRejected = (rejectedItems ?? []).some(
    (i) => i.name === "pan_number",
  );

  const [urls, setUrls] = useState<UrlState>(INITIAL_URLS);
  const [localUris, setLocalUris] = useState<
    Partial<Record<UploadKey, string>>
  >({});
  const [uploadingKeys, setUploadingKeys] = useState<Set<UploadKey>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aadhaarNum, setAadhaarNum] = useState(
    normalizeAadhaar(initialAadhaar),
  );
  const [panNum, setPanNum] = useState(normalizePan(initialPan));

  const aadhaarNumValid = !showAadhaarNumber || AADHAAR_REGEX.test(aadhaarNum);
  const panNumValid = !showPanNumber || PAN_REGEX.test(panNum);
  const aadhaarNumChanged =
    showAadhaarNumber && aadhaarNum !== normalizeAadhaar(initialAadhaar);
  const panNumChanged = showPanNumber && panNum !== normalizePan(initialPan);

  // Valid when: numbers pass format, every rejected IMAGE tile is re-uploaded,
  // and there is at least one actual change (an image or an edited number) — so
  // a number-only fix (no rejected image) is submittable, but an empty no-op is
  // not.
  const isFormValid = useMemo(() => {
    if (!aadhaarNumValid || !panNumValid) return false;
    const allRejectedImagesUploaded = [...rejectedKeys].every(
      (key) => !!urls[KEY_TO_URL_FIELD[key]],
    );
    const anyChange =
      Object.values(urls).some(Boolean) || aadhaarNumChanged || panNumChanged;
    return allRejectedImagesUploaded && anyChange;
  }, [
    rejectedKeys,
    urls,
    aadhaarNumValid,
    panNumValid,
    aadhaarNumChanged,
    panNumChanged,
  ]);

  const setUploading = useCallback((key: UploadKey, on: boolean) => {
    setUploadingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const uploadAsset = useCallback(
    async (asset: {
      uri?: string;
      fileName?: string | null;
      type?: string | null;
    }): Promise<string | null> => {
      if (!asset.uri) return null;
      try {
        return await uploadOnboardingImage(
          {
            uri: asset.uri,
            name: asset.fileName ?? `upload_${Date.now()}`,
            type: asset.type ?? "image/jpeg",
          },
          phoneNumber,
        );
      } catch (err) {
        const axiosError = err as {
          response?: { data?: { message?: string } };
        };
        toast.showLongToast(
          axiosError?.response?.data?.message || "Upload failed. Try again.",
        );
        return null;
      }
    },
    [phoneNumber],
  );

  const pickAndUpload = useCallback(
    async (uploadKey: UploadKey, urlField: keyof UrlState, isPhoto = false) => {
      try {
        const asset = isPhoto
          ? await pickImageFromCamera("front")
          : await pickImageWithChoice();
        if (!asset) return;

        if (asset.uri) {
          setLocalUris((prev) => ({ ...prev, [uploadKey]: asset.uri }));
        }
        setUploading(uploadKey, true);
        const url = await uploadAsset(asset);
        setUploading(uploadKey, false);
        if (!url) return;
        setUrls((prev) => ({ ...prev, [urlField]: url }));
      } catch {
        toast.showLongToast("Failed to pick image. Please try again.");
      }
    },
    [setUploading, uploadAsset],
  );

  const onSubmit = useCallback(async () => {
    if (isSubmitting || !isFormValid) return;
    setIsSubmitting(true);
    try {
      await resubmitDocuments({
        aadhaarFrontUrl: urls.aadhaarFront ?? undefined,
        aadhaarBackUrl: urls.aadhaarBack ?? undefined,
        panFrontUrl: urls.panFront ?? undefined,
        panBackUrl: urls.panBack ?? undefined,
        drivingLicenseFrontUrl: urls.dlFront ?? undefined,
        drivingLicenseBackUrl: urls.dlBack ?? undefined,
        selfieUrl: urls.selfie ?? undefined,
        personalDocFrontUrl: urls.personalDocFront ?? undefined,
        personalDocBackUrl: urls.personalDocBack ?? undefined,
        aadhaarNumber: showAadhaarNumber ? aadhaarNum : undefined,
        panNumber: showPanNumber ? panNum : undefined,
      });
      toast.showLongToast("Documents submitted for review.");
      navigation.goBack();
    } catch (err) {
      const axiosError = err as {
        response?: { data?: { message?: string } };
      };
      toast.showLongToast(
        axiosError?.response?.data?.message ||
          "Submission failed. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    isFormValid,
    urls,
    navigation,
    showAadhaarNumber,
    aadhaarNum,
    showPanNumber,
    panNum,
  ]);

  const renderUploadBox = (
    uploadKey: UploadKey,
    urlField: keyof UrlState,
    label: string,
    isPhoto = false,
  ) => {
    const uploaded = !!urls[urlField];
    const uploading = uploadingKeys.has(uploadKey);
    const isRejected = rejectedKeys.has(uploadKey);
    const localUri = localUris[uploadKey];

    return (
      <Pressable
        style={[
          styles.uploadBox,
          uploaded && styles.uploadBoxFilled,
          isRejected && !uploaded && boxStyles.rejectedBorder,
        ]}
        onPress={() =>
          !uploading && pickAndUpload(uploadKey, urlField, isPhoto)
        }
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        {uploading ? (
          <ActivityIndicator color={color.primary} />
        ) : uploaded && localUri ? (
          <>
            <Image
              source={{ uri: localUri }}
              style={docStyles.thumbnail}
              resizeMode="cover"
            />
            <View style={docStyles.thumbnailBadge}>
              <Text style={docStyles.thumbnailBadgeText}>✓</Text>
            </View>
          </>
        ) : uploaded ? (
          <>
            <Text style={styles.uploadCheckmark}>✓</Text>
            <Text style={styles.uploadedFileName} numberOfLines={2}>
              Uploaded
            </Text>
          </>
        ) : (
          <>
            <Text
              style={[
                styles.uploadBoxIcon,
                isRejected && boxStyles.rejectedIcon,
              ]}
            >
              {isRejected ? "✕" : "↑"}
            </Text>
            <Text
              style={[styles.uploadText, isRejected && boxStyles.rejectedLabel]}
            >
              {label}
            </Text>
          </>
        )}
      </Pressable>
    );
  };

  const getSectionComment = (keys: UploadKey[]): string | null => {
    for (const key of keys) {
      if (rejectionComments[key]) return rejectionComments[key]!;
    }
    return null;
  };

  // Editable Aadhaar / PAN number field, shown inside the matching doc section.
  const renderNumberField = (docId: DocumentId) => {
    if (docId === "aadhaarCard" && showAadhaarNumber) {
      const invalid = aadhaarNum.length > 0 && !AADHAAR_REGEX.test(aadhaarNum);
      const flag = invalid || aadhaarNumRejected;
      return (
        <View style={boxStyles.numberField}>
          <Text
            style={[
              styles.documentLabel,
              aadhaarNumRejected && boxStyles.rejectedSectionLabel,
            ]}
          >
            Aadhaar Number<Text style={styles.requiredStar}>*</Text>
            {aadhaarNumRejected && (
              <Text style={boxStyles.rejectedTag}> · Please correct</Text>
            )}
          </Text>
          <TextInput
            style={[boxStyles.numberInput, flag && boxStyles.numberInputError]}
            value={aadhaarNum}
            onChangeText={(txt) => setAadhaarNum(normalizeAadhaar(txt))}
            keyboardType="numeric"
            maxLength={12}
            placeholder="1234 5678 9012"
            placeholderTextColor={color.Gray20}
          />
          {invalid && (
            <Text style={boxStyles.rejectionComment}>
              Enter a valid 12-digit Aadhaar number
            </Text>
          )}
        </View>
      );
    }
    if (docId === "panCard" && showPanNumber) {
      const invalid = panNum.length > 0 && !PAN_REGEX.test(panNum);
      const flag = invalid || panNumRejected;
      return (
        <View style={boxStyles.numberField}>
          <Text
            style={[
              styles.documentLabel,
              panNumRejected && boxStyles.rejectedSectionLabel,
            ]}
          >
            PAN Number<Text style={styles.requiredStar}>*</Text>
            {panNumRejected && (
              <Text style={boxStyles.rejectedTag}> · Please correct</Text>
            )}
          </Text>
          <TextInput
            style={[boxStyles.numberInput, flag && boxStyles.numberInputError]}
            value={panNum}
            onChangeText={(txt) => setPanNum(normalizePan(txt))}
            autoCapitalize="characters"
            maxLength={10}
            placeholder="ABCDE1234F"
            placeholderTextColor={color.Gray20}
          />
          {invalid && (
            <Text style={boxStyles.rejectionComment}>
              Enter a valid PAN (e.g. ABCDE1234F)
            </Text>
          )}
        </View>
      );
    }
    return null;
  };

  const renderDocSection = (
    docId: DocumentId,
    label: string,
    required: boolean,
  ) => {
    const { front, back } = DOC_KEY_MAP[docId];
    const isSectionRejected =
      rejectedKeys.has(front.uploadKey) || rejectedKeys.has(back.uploadKey);
    const comment = getSectionComment([front.uploadKey, back.uploadKey]);

    return (
      <View key={docId} style={styles.documentSection}>
        <Text
          style={[
            styles.documentLabel,
            isSectionRejected && boxStyles.rejectedSectionLabel,
          ]}
        >
          {label}
          {required && <Text style={styles.requiredStar}>*</Text>}
          {isSectionRejected && (
            <Text style={boxStyles.rejectedTag}> · Needs re-upload</Text>
          )}
        </Text>
        <View style={styles.uploadRow}>
          {renderUploadBox(front.uploadKey, front.urlField, "Upload Front")}
          {renderUploadBox(back.uploadKey, back.urlField, "Upload Back")}
        </View>
        {comment && <Text style={boxStyles.rejectionComment}>{comment}</Text>}
        {renderNumberField(docId)}
      </View>
    );
  };

  const isSelfieRejected = rejectedKeys.has("photo");
  const selfieComment = rejectionComments.photo;

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Re-upload Documents"
        navigation={navigation}
        hideShadow
      />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {rejectedKeys.size > 0 && (
          <View style={boxStyles.rejectionBanner}>
            <Text style={boxStyles.rejectionBannerText}>
              {rejectedKeys.size === 1
                ? "1 document was rejected."
                : `${rejectedKeys.size} documents were rejected.`}{" "}
              Re-upload the highlighted sections to continue.
            </Text>
          </View>
        )}

        {documentConfigs.map(({ id, label, required }) =>
          renderDocSection(id, label, required),
        )}

        {/* Selfie */}
        <View style={styles.documentSection}>
          <Text
            style={[
              styles.documentLabel,
              isSelfieRejected && boxStyles.rejectedSectionLabel,
            ]}
          >
            Upload Photo
            <Text style={styles.requiredStar}>*</Text>
            {isSelfieRejected && (
              <Text style={boxStyles.rejectedTag}> · Needs re-upload</Text>
            )}
          </Text>
          <Pressable
            style={[
              styles.photoUploadBox,
              urls.selfie && styles.uploadBoxFilled,
              isSelfieRejected && !urls.selfie && boxStyles.rejectedBorder,
            ]}
            onPress={() => pickAndUpload("photo", "selfie", true)}
            accessibilityRole="button"
            accessibilityLabel="Take a photo"
          >
            {urls.selfie && localUris.photo ? (
              <>
                <Image
                  source={{ uri: localUris.photo }}
                  style={docStyles.thumbnail}
                  resizeMode="cover"
                />
                <View style={docStyles.thumbnailBadge}>
                  <Text style={docStyles.thumbnailBadgeText}>✓</Text>
                </View>
              </>
            ) : urls.selfie ? (
              <>
                <Text style={styles.uploadCheckmark}>✓</Text>
                <Text style={styles.uploadedFileName}>Photo uploaded</Text>
              </>
            ) : (
              <>
                <Text style={styles.uploadEmoji}>
                  {isSelfieRejected ? "✕" : "📷"}
                </Text>
                <Text
                  style={[
                    styles.uploadText,
                    isSelfieRejected && boxStyles.rejectedLabel,
                  ]}
                >
                  Take Photo
                </Text>
              </>
            )}
          </Pressable>
          {selfieComment && (
            <Text style={boxStyles.rejectionComment}>{selfieComment}</Text>
          )}
        </View>

        {isFamilyKyc &&
          (() => {
            const isFrontRejected = rejectedKeys.has("personalDoc:front");
            const isBackRejected = rejectedKeys.has("personalDoc:back");
            const isSectionRejected = isFrontRejected || isBackRejected;
            const comment = getSectionComment([
              "personalDoc:front",
              "personalDoc:back",
            ]);
            return (
              <View style={styles.documentSection}>
                <Text
                  style={[
                    styles.documentLabel,
                    isSectionRejected && boxStyles.rejectedSectionLabel,
                  ]}
                >
                  Personal Document (Family)
                  <Text style={styles.requiredStar}>*</Text>
                  {isSectionRejected && (
                    <Text style={boxStyles.rejectedTag}>
                      {" "}
                      · Needs re-upload
                    </Text>
                  )}
                </Text>
                <View style={styles.uploadRow}>
                  {renderUploadBox(
                    "personalDoc:front",
                    "personalDocFront",
                    "Upload Front",
                  )}
                  {renderUploadBox(
                    "personalDoc:back",
                    "personalDocBack",
                    "Upload Back",
                  )}
                </View>
                {comment && (
                  <Text style={boxStyles.rejectionComment}>{comment}</Text>
                )}
              </View>
            );
          })()}
      </ScrollView>

      <View style={styles.bottomBar}>
        <Pressable
          style={[
            commonStyles.primaryButton,
            (!isFormValid || isSubmitting) &&
              commonStyles.primaryButtonDisabled,
          ]}
          disabled={!isFormValid || isSubmitting}
          onPress={onSubmit}
          accessibilityRole="button"
          accessibilityLabel="Submit documents"
        >
          <Text variant="title_16" style={commonStyles.primaryButtonText}>
            {isSubmitting ? "Submitting..." : "Submit Documents"}
          </Text>
        </Pressable>
      </View>
    </>
  );
};

const boxStyles = StyleSheet.create({
  rejectedBorder: {
    borderWidth: 1.5,
    borderColor: color.errorRed,
  },
  rejectedIcon: {
    color: color.errorRed,
  },
  rejectedLabel: {
    color: color.errorRed,
  },
  rejectedSectionLabel: {
    color: color.errorRed,
  },
  rejectedTag: {
    fontSize: 12,
    color: color.errorRed,
  },
  rejectionComment: {
    fontSize: 12,
    color: color.errorRed,
    marginTop: 6,
    lineHeight: 16,
  },
  numberField: {
    marginTop: 12,
  },
  numberInput: {
    borderWidth: 1,
    borderColor: color.borderColor3,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 6,
    fontSize: 15,
    color: color.black,
  },
  numberInputError: {
    borderColor: color.errorRed,
  },
  rejectionBanner: {
    backgroundColor: color.lightRed,
    borderRadius: 10,
    padding: 12,
    marginBottom: 20,
  },
  rejectionBannerText: {
    fontSize: 13,
    color: color.errorRed,
    lineHeight: 18,
  },
});

const docStyles = StyleSheet.create({
  thumbnail: {
    ...StyleSheet.absoluteFill,
    borderRadius: 7,
  },
  thumbnailBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: color.plain_green,
    alignItems: "center",
    justifyContent: "center",
  },
  thumbnailBadgeText: {
    color: color.white,
    fontSize: 11,
    lineHeight: 13,
  },
});

export default ReuploadDocuments;
